import React, { useEffect, useState } from 'react'
import ProductRightPanel from '../../../components/Product/ProductRightPanel/ProductRightPanel'
import { useReflectionPickerDataQuery } from '@graphql/queries/__generated__/product-details.generated'
import {
  useCartUpdateProductKitMutation,
  useCartUpdateProductVariantMutation,
} from '@graphql/mutations/__generated__/product-reflection.generated'
import { useCartLinesQuery } from '@graphql/queries/__generated__/cart.generated'
import { useSnackbar } from 'notistack'
import VariantKitPanel from '../../../components/Product/VariantKitPanel/VariantKitPanel'
import { useVariantKitDataQuery } from '@graphql/queries/__generated__/product-variant-kit.generated'
import { CartTypeEnum } from '../../../../generated/gqlTypes'
import { NumberParam, useQueryParams } from 'use-query-params'
import { useTranslation } from 'react-i18next'
import { syncCartCache } from '../../../utils/cartCache'

export const EditCartLineQueryParams = {
  editLinePk: NumberParam,
}

// The single-line `cartLine(id)` query only accepts a global ID, but several entry points into this
// panel (wholesale flows) only have the line's numeric pk on hand, so we resolve it from the list instead.
const MAX_CART_LINES = 1000

interface CartLineEditPanelProps {
  setLoading(linePk: number | null): void

  cartType?: CartTypeEnum
}

const CartLineEditPanel: React.FC<CartLineEditPanelProps> = ({ setLoading, cartType = CartTypeEnum.Default }) => {
  const { t } = useTranslation()
  const [params, setParams] = useQueryParams(EditCartLineQueryParams)
  const linePk = params.editLinePk
  const { enqueueSnackbar } = useSnackbar()

  const [productPickerOpen, setProductPickerOpen] = useState(linePk)
  const [isLoading, setIsLoading] = useState(false)
  const cartLinesQueryResult = useCartLinesQuery({
    variables: {
      cartType,
      limit: MAX_CART_LINES,
      offset: 0,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !linePk,
  })
  const cartLine = cartLinesQueryResult.data?.cartLines.results.find(line => line.pk === linePk)

  const isKit = !!cartLine?.supplyParts.length
  const refelectionPickerDataQueryResult = useReflectionPickerDataQuery({
    variables: {
      id: cartLine?.variant.product.siteSpecifics[0]?.id,
    },
    skip: !cartLine || isKit,
  })
  const dataKitQuery = useVariantKitDataQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    variables: {
      pk: cartLine?.variant.pk,
      cartLine: linePk,
      cartType,
    },
    skip: !cartLine || !isKit,
  })
  // Both mutations return the refreshed cart. The line list is refetched only when a variant swap replaced
  // the line, or after a kit edit, which recreates the kit's supply parts.
  const [cartUpdateVariant] = useCartUpdateProductVariantMutation({
    awaitRefetchQueries: true,
    refetchQueries: ({ data }) => (data?.cartUpdateVariant.cartLine ? [] : ['CartLines']),
    update: (cache, { data }) => syncCartCache(cache, data?.cartUpdateVariant.cart),
  })
  const [cartUpdateKit] = useCartUpdateProductKitMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['CartLines'],
    update: (cache, { data }) => syncCartCache(cache, data?.cartUpdateKit.cart),
  })
  useEffect(() => {
    setProductPickerOpen(linePk)
  }, [linePk])

  const handleClosePanel = () => {
    if (isLoading) {
      return
    }
    setProductPickerOpen(null)
    setParams({ editLinePk: undefined }, 'replaceIn')
  }
  const updateBag = async (variantPk: number, qty: number) => {
    setLoading(linePk)
    setIsLoading(true)
    try {
      await cartUpdateVariant({
        variables: {
          variant: variantPk,
          cartLine: linePk,
          quantity: qty,
          cartType,
          includeLinesByCategory: true,
        },
      })
      enqueueSnackbar(
        t('snackbarMessages.success.shoppingBagModified', 'Your cart has been modified successfully!'),
        {
          variant: 'success',
        }
      )
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLoading(null)
    setIsLoading(false)
    handleClosePanel()
  }

  const onSubmitKit = async (supplyLine, variants, setAsDefault = false) => {
    setLoading(linePk)
    setIsLoading(true)
    try {
      await cartUpdateKit({
        variables: {
          cartLine: supplyLine,
          cartType,
          variants: variants.map(el => ({
            pk: el.pk,
            quantity: el.quantity,
          })),
          setAsDefault,
          includeLinesByCategory: true,
        },
      })
      enqueueSnackbar(
        t('snackbarMessages.success.shoppingBagModified', 'Your cart has been modified successfully!'),
        {
          variant: 'success',
        }
      )
    } catch (e) {
      enqueueSnackbar(e.message, {
        variant: 'error',
      })
    }
    setLoading(null)
    setIsLoading(false)
    handleClosePanel()
  }

  const kitData = dataKitQuery.data ? dataKitQuery.data.productVariant.customizeKitData : null
  if (!cartLine) return null

  return (
    <>
      <ProductRightPanel
        isOpen={!!productPickerOpen && !isKit}
        onClosePanel={handleClosePanel}
        loading={refelectionPickerDataQueryResult.loading || cartLinesQueryResult.loading || isLoading}
        pickerDataJson={refelectionPickerDataQueryResult.data?.reflectionDetails?.reflection}
        updateBag={updateBag}
        onOpenPanel={() => setProductPickerOpen(productPickerOpen)}
        qty={cartLine.quantity}
        selectedVariantPk={cartLine.variant.pk}
      />
      <VariantKitPanel
        isOpen={!!productPickerOpen && isKit}
        onClosePanel={handleClosePanel}
        onOpen={() => setProductPickerOpen(productPickerOpen)}
        onSubmit={onSubmitKit}
        loading={dataKitQuery.loading || cartLinesQueryResult.loading || isLoading}
        data={dataKitQuery.loading ? null : kitData}
        isWholesale={cartType === CartTypeEnum.Wholesale}
      />
    </>
  )
}

export default CartLineEditPanel
